/**
 * Tour Guide Wrapper Component
 * Provides a reusable tour guide implementation with beautiful styling
 */

import { useEffect, useRef, useCallback } from 'react';
import { TourGuideClient } from '@sjmc11/tourguidejs';
import '@sjmc11/tourguidejs/src/scss/tour.scss';
import './TourGuide.css';

/**
 * Utility function to clean up tour guide DOM elements and localStorage
 */
const cleanupTourDOM = (tourGroup = null) => {
  try {
    // Remove backdrop
    const tourBackdrop = document.querySelector('.tg-backdrop');
    if (tourBackdrop && tourBackdrop.parentNode) {
      tourBackdrop.parentNode.removeChild(tourBackdrop);
    }
    
    // Remove dialog
    const tourDialog = document.querySelector('.tg-dialog');
    if (tourDialog && tourDialog.parentNode) {
      tourDialog.parentNode.removeChild(tourDialog);
    }
    
    // Remove body class
    document.body.classList.remove('tg-active');
    
    // Remove any other tour elements (but preserve data-tour-id elements)
    const tourElements = document.querySelectorAll('[class*="tg-"]');
    tourElements.forEach(element => {
      if (element && element.parentNode && !element.hasAttribute('data-tour-id')) {
        element.parentNode.removeChild(element);
      }
    });
    
    // Remove overflow hidden from body if present
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
    
    // Clear localStorage for tour if group provided
    if (tourGroup) {
      try {
        localStorage.removeItem(`tg-${tourGroup}-finished`);
        localStorage.removeItem(`tg-${tourGroup}-step`);
        localStorage.removeItem(`tg-${tourGroup}-active`);
      } catch (storageError) {
        console.warn('Error clearing tour localStorage:', storageError);
      }
    }
  } catch (error) {
    console.warn('Error during tour DOM cleanup:', error);
  }
};

/**
 * Utility function to check if tour target elements exist in DOM
 */
const waitForTourTargets = (steps, timeout = 2000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkTargets = () => {
      // Check first few critical target elements (not all to be less strict)
      const criticalSteps = steps.slice(0, Math.min(3, steps.length));
      const criticalTargetsExist = criticalSteps.every(step => {
        if (!step.target) return true;
        const element = document.querySelector(step.target);
        return element !== null;
      });
      
      if (criticalTargetsExist) {
        resolve(true);
      } else if (Date.now() - startTime < timeout) {
        // Keep checking every 50ms (faster polling)
        setTimeout(checkTargets, 50);
      } else {
        console.warn('Some critical tour target elements not found, initializing anyway');
        resolve(true); // Resolve true anyway to allow initialization
      }
    };
    
    checkTargets();
  });
};

/**
 * Custom hook to initialize and manage tour guide
 * @param {Array} steps - Tour steps configuration
 * @param {Object} options - Tour guide options
 * @param {string} tourGroup - Tour group identifier
 * @param {boolean} autoStart - Whether to start tour automatically
 */
export const useTourGuide = (steps, options, tourGroup = 'tour', autoStart = false) => {
  const tourRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isMountedRef = useRef(true);
  const isStartingRef = useRef(false); // Track if tour is currently starting
  const isActiveRef = useRef(false); // Track if tour is currently active

  useEffect(() => {
    isMountedRef.current = true;
    let initTimer = null;

    // Only initialize if steps are available
    if (!steps || steps.length === 0) {
      return;
    }

    // Clean up any existing tour instance before reinitializing
    if (tourRef.current) {
      try {
        tourRef.current.exit();
      } catch (error) {
        console.warn('Error cleaning up previous tour instance:', error);
      }
      tourRef.current = null;
    }

    // Clean up any leftover DOM elements from previous tours
    cleanupTourDOM(tourGroup);

    // Initialize tour after ensuring DOM elements are ready
    const initializeTour = async () => {
      if (!isMountedRef.current) return;
      
      try {
        // Wait for critical target elements to be available in DOM
        await waitForTourTargets(steps);
        
        if (!isMountedRef.current) return;
        
        // Additional small delay to ensure everything is settled
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!isMountedRef.current) return;
        
        // Initialize tour guide with fresh steps array
        const freshSteps = steps.map(step => ({ ...step }));
        
        const tourGuide = new TourGuideClient({
          ...options,
          steps: freshSteps,
          group: tourGroup,
        });

        // Add event listeners to track tour lifecycle
        tourGuide.onFinish(() => {
          console.log('Tour finished');
          isActiveRef.current = false;
          isStartingRef.current = false;
        });

        tourGuide.onAfterExit(() => {
          console.log('Tour exited');
          isActiveRef.current = false;
          isStartingRef.current = false;
        });

        tourRef.current = tourGuide;
        isInitializedRef.current = true;

        console.log('Tour guide initialized successfully with', freshSteps.length, 'steps');

        // Auto-start if enabled and tour not completed
        if (autoStart && !tourGuide.isFinished) {
          setTimeout(() => {
            if (isMountedRef.current && tourRef.current) {
              tourGuide.start();
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error initializing tour guide:', error);
        // Even if there's an error, try to initialize anyway
        try {
          if (isMountedRef.current) {
            const freshSteps = steps.map(step => ({ ...step }));
            const tourGuide = new TourGuideClient({
              ...options,
              steps: freshSteps,
              group: tourGroup,
            });

            // Add event listeners for fallback initialization too
            tourGuide.onFinish(() => {
              console.log('Tour finished (fallback)');
              isActiveRef.current = false;
              isStartingRef.current = false;
            });

            tourGuide.onAfterExit(() => {
              console.log('Tour exited (fallback)');
              isActiveRef.current = false;
              isStartingRef.current = false;
            });

            tourRef.current = tourGuide;
            isInitializedRef.current = true;
            console.log('Tour initialized via fallback');
          }
        } catch (fallbackError) {
          console.error('Fallback initialization also failed:', fallbackError);
        }
      }
    };

    // Small delay before starting initialization process
    initTimer = setTimeout(() => {
      initializeTour();
    }, 300);

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      
      // Clear initialization timer
      if (initTimer) {
        clearTimeout(initTimer);
      }
      
      // Comprehensive cleanup
      if (tourRef.current) {
        try {
          // Exit the tour
          tourRef.current.exit();
        } catch (error) {
          console.warn('Error exiting tour guide:', error);
        }
        
        tourRef.current = null;
      }
      
      // Clean up all DOM elements
      cleanupTourDOM(tourGroup);
      
      // Reset all state flags
      isInitializedRef.current = false;
      isActiveRef.current = false;
      isStartingRef.current = false;
    };
  }, [steps, options, tourGroup, autoStart]);

  const startTour = useCallback(async () => {
    // Prevent double-clicking and multiple simultaneous starts
    if (isStartingRef.current || isActiveRef.current) {
      console.log('Tour is already starting or active, ignoring additional click');
      return;
    }

    if (!isMountedRef.current) {
      console.warn('Component unmounted, cannot start tour');
      return;
    }

    // Mark tour as starting
    isStartingRef.current = true;

    if (!tourRef.current) {
      console.warn('Tour not initialized yet, waiting...');
      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (!tourRef.current || !isMountedRef.current) {
        console.error('Tour still not initialized after waiting');
        isStartingRef.current = false; // Reset flag
        return;
      }
    }
    
    try {
      // Check if tour is already active (via library's internal state)
      if (tourRef.current.isActive) {
        console.log('Tour is already active, ignoring start request');
        isStartingRef.current = false;
        isActiveRef.current = true;
        return;
      }

      // Verify all target elements exist before starting
      const allTargetsValid = tourRef.current.steps?.every(step => {
        if (!step?.target) return true;
        const element = document.querySelector(step.target);
        if (!element) {
          console.warn(`Target not found: ${step.target}`);
          return false;
        }

        const rect = element.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;
        if (!isVisible) {
          console.warn(`Target has no visible size: ${step.target}`);
        }

        return isVisible;
      });
      
      if (!allTargetsValid) {
        console.warn('Some targets not ready, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Start the tour
      console.log('Starting tour with', tourRef.current.steps?.length, 'steps');
      tourRef.current.start();
      
      // Ensure positions are recalculated once dialog is rendered
      requestAnimationFrame(() => {
        try {
          tourRef.current?.refresh?.();
          tourRef.current?.updatePositions?.();
        } catch (refreshError) {
          console.warn('Error refreshing tour positions:', refreshError);
        }
      });
      
      // Mark tour as active
      isActiveRef.current = true;
    } catch (error) {
      console.error('Error starting tour:', error);
      // If there's an error with "already active", just silently handle it
      if (error.message && error.message.toLowerCase().includes('already active')) {
        console.log('Tour already active, handled gracefully');
        isActiveRef.current = true;
      } else {
        // Reset flags on error
        isStartingRef.current = false;
        isActiveRef.current = false;
      }
    } finally {
      // Reset starting flag after a short delay (only if not already reset by error)
      if (isStartingRef.current) {
        setTimeout(() => {
          isStartingRef.current = false;
        }, 500);
      }
    }
  }, []);

  const exitTour = useCallback(() => {
    if (tourRef.current) {
      try {
        tourRef.current.exit();
      } catch (error) {
        console.error('Error exiting tour:', error);
      }
    }
    // Reset state flags
    isActiveRef.current = false;
    isStartingRef.current = false;
    
    // Clean up DOM elements after exiting
    cleanupTourDOM(tourGroup);
  }, [tourGroup]);

  const nextStep = useCallback(() => {
    if (tourRef.current && isMountedRef.current) {
      try {
        tourRef.current.nextStep();
      } catch (error) {
        console.error('Error going to next step:', error);
      }
    }
  }, []);

  const prevStep = useCallback(() => {
    if (tourRef.current && isMountedRef.current) {
      try {
        tourRef.current.prevStep();
      } catch (error) {
        console.error('Error going to previous step:', error);
      }
    }
  }, []);

  const visitStep = useCallback((stepIndex) => {
    if (tourRef.current && isMountedRef.current) {
      try {
        tourRef.current.visitStep(stepIndex);
      } catch (error) {
        console.error('Error visiting step:', error);
      }
    }
  }, []);

  const resetTour = useCallback(() => {
    if (tourRef.current && tourGroup && isMountedRef.current) {
      try {
        // Clear localStorage for this tour group
        localStorage.removeItem(`tg-${tourGroup}-finished`);
        localStorage.removeItem(`tg-${tourGroup}-step`);
        // Restart tour
        tourRef.current.start();
      } catch (error) {
        console.error('Error resetting tour:', error);
      }
    }
  }, [tourGroup]);

  return {
    startTour,
    exitTour,
    nextStep,
    prevStep,
    visitStep,
    resetTour,
    tourGuide: tourRef.current,
  };
};

export default useTourGuide;